import axios from "axios";
import { useState, useEffect, useMemo } from "react";
import { Colors } from "./Colors";

const TABLET_WIDTH = 744;
const FULL_WIDTH = 1280;

const getState = () => {
	let height = 0;
	let width = 0;
	if (typeof window !== "undefined") {
		height = window.innerHeight;
		width = window.innerWidth;
	}
	return {
		width,
		height,
		isMobile: width < TABLET_WIDTH,
		isTablet: width < FULL_WIDTH,
		isDesktop: width >= FULL_WIDTH,
	};
};

/**
 * A hook for keeping track window dimensions
 *
 * Returns object { width: number, height: number, resizeBreakPoint: number }
 * resizeBreakPoint is the point of transition between desktop and mobile
 */
export function useWindowSize() {
	// for production build default set as desktop size
	const [windowSize, setWindowSize] = useState({
		width: 1280,
		height: 720,
		isMobile: false,
		isTablet: false,
		isDesktop: true,
	});
	// https://www.gatsbyjs.com/docs/debugging-html-builds/
	const isBrowser = typeof window !== "undefined";
	if (isBrowser) {
		useEffect(() => {
			// Handler to call on window resize
			function handleResize() {
				// Set window width/height to state
				setWindowSize(getState());
			}

			// Add event listener
			window.addEventListener("resize", handleResize);

			// Call handler right away so state gets updated with initial window size
			handleResize();

			// Remove event listener on cleanup
			return () => window.removeEventListener("resize", handleResize);
		}, []); // Empty array ensures that effect is only run on mount
	}

	return windowSize;
}

const WindowSizeBreakPoint = {
	mobile: 600,
	smallTablet: 905,
	tablet: 1280,
	laptop: 1668,
};
export type WindowSizeType =
	| "mobile"
	| "tablet_small"
	| "tablet"
	| "laptop"
	| "desktop";
/**
 * A hook for keeping track window type depend on window size
 *
 * Returns windowSizeType: WindowSizeType
 * default is "laptop" if window is not defined.
 */
export function useWindowSizeType() {
	const [windowSizeType, setWindowSizeType] =
		useState<WindowSizeType>("laptop");
	// https://www.gatsbyjs.com/docs/debugging-html-builds/
	const isBrowser = typeof window !== "undefined";
	if (isBrowser) {
		useEffect(() => {
			// Handler to call on window resize
			function handleResize() {
				let type: WindowSizeType = "laptop";
				const width = window.innerWidth;
				if (width < WindowSizeBreakPoint.mobile) {
					type = "mobile";
				} else if (width < WindowSizeBreakPoint.smallTablet) {
					type = "tablet_small";
				} else if (width < WindowSizeBreakPoint.tablet) {
					type = "tablet";
				} else if (width < WindowSizeBreakPoint.laptop) {
					type = "laptop";
				} else {
					type = "desktop";
				}
				setWindowSizeType(type);
			}

			// Add event listener
			window.addEventListener("resize", handleResize);

			// Call handler right away so state gets updated with initial window size
			handleResize();

			// Remove event listener on cleanup
			return () => window.removeEventListener("resize", handleResize);
		}, []);
	}

	return windowSizeType;
}

export const useOnScroll = (callback: (ev: Event) => any) => {
	const wrapped = (ev: Event) => callback(ev);
	useEffect(() => {
		window.addEventListener("scroll", wrapped);
		return () => window.removeEventListener("scroll", wrapped);
	}, []);
};

const ERROR_TIMEOUT = 500;

export const useHubspotSubmission = <T extends Record<any, any>>(
	form: T,
	hubspotUrl: string
) => {
	const [submitStatus, setSubmitStatus] = useState<
		"NOT_SUBMITTED" | "SUBMITTING" | "SUCCESS" | "ERROR"
	>("NOT_SUBMITTED");

	const submitForm = () => {
		const formBody = {
			fields: Object.entries(form).map(([name, value]) => ({
				name,
				value,
			})),
		};
		setSubmitStatus("SUBMITTING");

		axios
			.post(hubspotUrl, formBody)
			.then(() => setSubmitStatus("SUCCESS"))
			.catch(() => {
				setSubmitStatus("ERROR");
				setTimeout(
					() => setSubmitStatus("NOT_SUBMITTED"),
					ERROR_TIMEOUT
				);
			});
	};
	return { submitForm, submitStatus, setSubmitStatus };
};

export const useHubspotForm = <T extends Record<string, string>>(
	initialState: T,
	hubspotUrl: string
) => {
	const [form, setForm] = useState(initialState);
	const [validityState, setValidityState] = useState(
		Object.keys(initialState).reduce(
			(obj, key) => ({ ...obj, [key]: false }),
			{} as Record<keyof T, boolean>
		)
	);
	const [forceShowError, setForceShowError] = useState(false);
	const { submitForm, submitStatus, setSubmitStatus } = useHubspotSubmission(
		form,
		hubspotUrl
	);

	const initForm = () => {
		setForm(initialState);
		setSubmitStatus("NOT_SUBMITTED");
		setForceShowError(false);
	};

	const inputProps = (key: keyof T) => ({
		value: form[key],
		forceShowError,
		onValidityChange: (valid: boolean) =>
			setValidityState((validityState) => ({
				...validityState,
				[key]: valid,
			})),
		onChange: (value: string) =>
			setForm((form) => ({
				...form,
				[key]: value,
			})),
	});

	const handleSubmit = () => {
		setForceShowError(true);
		const anyAreInvalid = Object.values(validityState).includes(false);
		if (anyAreInvalid) return;

		submitForm();
	};

	return { inputProps, handleSubmit, submitStatus, initForm };
};

/**
 * useTrackSidebar hook is to keep tracing the headers
 * and it will active sidebar (change color with activeColor)
 * if sidebar header is the same y-position of the header
 * note: all headers and sidebar header have to have matched id.
 * For example, if header name is `overview`,
 * sideber's header id have to be `sidebar-overview`
 */
export const useTrackSidebar = (
	headers: string[],
	_activeColor: Colors,
	_defaultColor: Colors
) => {
	const reversedHeaders = useMemo(() => [...headers].reverse(), [headers]);
	const activeColor = `var(--${_activeColor})`;
	const defaultColor = `var(--${_defaultColor})`;
	const checkHeader = () => {
		const activeHeader = reversedHeaders.find((header) => {
			const headerRect = document
				.getElementById(header)
				?.getBoundingClientRect();
			const sidebarHeaderRect = document
				.getElementById(`sidebar-${header}`)
				?.getBoundingClientRect();
			if (!headerRect || !sidebarHeaderRect) return false;
			return headerRect.top - headerRect.height < sidebarHeaderRect.top;
		});
		reversedHeaders.forEach((header) => {
			const el = document.getElementById(`sidebar-${header}`);
			if (el) {
				el.style.color =
					header === activeHeader ? activeColor : defaultColor;
				el.style.fontWeight = header === activeHeader ? "500" : "400";
			}
		});
		if (!activeHeader) {
			const firstSidebarHeaderEl = document.getElementById(
				`sidebar-${headers[0]}`
			);
			if (!firstSidebarHeaderEl) {
				return;
			}
			firstSidebarHeaderEl.style.color = activeColor;
		}
	};
	useOnScroll(checkHeader);
};
export const useIsSSR = () => typeof window === "undefined";

export const useSsrRerender = () => {
	const [isClient, setClient] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			setClient(true);
		}, 300);
	}, []);
	return isClient;
};

export type PossibleObjectKey = string | number | symbol;

/**
 * This generic enforces that the value of "Obj" at "Field" can be a type literal (ie: string, symbol, number)
 */
type EnsureFieldValueIsTypeLiteral<
	Obj,
	Field extends keyof Obj
> = Obj[Field] extends PossibleObjectKey ? Obj[Field] : never;

/**
 * This generates a type that is an exhaustive mapping from some literal value to the literal value and the "narrowed"
 * object types that match the literal. Useful to build other types.
 * 
 * Eg: 
	type obj = {type: "A"} | {type: "B"} | {type: "C"};
	type mapping = MapLiteralFieldToNarrowedObj<obj, "type">;
	// Mapping: {
		A: {type: "A"},
		B: {type: "B"},
		C: {type: "C"}
	}
 */
type MapLiteralFieldToNarrowedObj<Obj, Field extends keyof Obj> = {
	[K in EnsureFieldValueIsTypeLiteral<Obj, Field>]: Extract<
		Obj,
		{ [F2 in Field]: K }
	>;
};

/**
 * Type safe helper to convert a list of objects to a map from the value of one of the object's keys to the objects.
 * Eg:
 * const arr = [{id: 1}, {id: 2}];
 *
 * mapToKeyValue(arr, "id"); // {1: {id: 1}, 2: {id: 2}}
 */
export const mapToKeyValue = <
	V extends PossibleObjectKey,
	K extends PossibleObjectKey,
	Arr extends Readonly<{ [Key in K]: V }[]>
>(
	arr: Arr,
	key: K
): MapLiteralFieldToNarrowedObj<Arr[number], K> =>
	arr.reduce(
		(mapping, obj: any) => ({ ...mapping, [obj[key] as any]: obj }),
		{}
	) as any;
